import {AxiosError} from 'axios';
import {ISecurityGroup} from '../../shared/types';
import {
  BackendErrorData,
  parseApiError,
} from '../../utils/custom-errors/parseApiError';
import {apiClient} from '../apiClient';
import {
  BulkAccessListRequest,
  BulkAccessListResponse,
  IGetRolesResponse,
  IDeleteUserRequestPayload,
  IGetUsersRequestPayload,
  IRemoveUserArgs,
  IGetUsersResponse,
  IUser,
  Project,
  SecurityGroupItem,
  IGetNotLoggedInUsersPayload,
  IGetNotLoggedInUsersResponse,
  IResendLinkRequestPayload,
  IResendLinkResponse,
  IGetMetaResponse,
  IAddUserPayload,
  IBulkUserAccessResponse,
  IGetBulkUserRightsPayload,
  IEditUserRequestPayload,
  IChangeUserRolePayload,
  IChangeUserRoleResponse,
  Domain,
  IUserDetailsResponse,
} from '../types/user.type';
import BASE_URL from '../../config/env';

const USER_MANAGEMENT_BASE_URL = `${BASE_URL}/usermanagement/api/v1/user`;

export const getUsers = async (
  payload: IGetUsersRequestPayload
): Promise<IGetUsersResponse> => {
  const {data} = await apiClient.post(
    `${USER_MANAGEMENT_BASE_URL}/users`,
    payload
  );
  return data;
};

export const createUser = async (payload: Partial<IUser>): Promise<IUser> => {
  const {data} = await apiClient.post('/users', payload);
  return data;
};

export const getProjectsByRoleAndEntity = async (
  roleID: number,
  entityID: number
): Promise<Project[]> => {
  const {data} = await apiClient.get(
    `${USER_MANAGEMENT_BASE_URL}/roles/${roleID}/entities/${entityID}/projects`
  );
  return data.projects;
};

export const getHeadersProjectsList = async (
  entityID: number
): Promise<Project[]> => {
  const {data} = await apiClient.get(
    `${USER_MANAGEMENT_BASE_URL}/entities/${entityID}/projects`
  );
  return data;
};

export const getDomainsByRoleAndEntity = async (
  roleID: number,
  entityID: number
): Promise<Domain[]> => {
  const {data} = await apiClient.get<{domains: Domain[]}>(
    `${USER_MANAGEMENT_BASE_URL}/roles/${roleID}/entities/${entityID}/domains`
  );
  return data.domains;
};

export const getRoles = async (): Promise<IGetRolesResponse> => {
  const {data} = await apiClient.get(`${USER_MANAGEMENT_BASE_URL}/role`);
  return data;
};

export const getSecurityGroupsByRoleAndProject = async (
  roleId: number,
  projectId: number
): Promise<SecurityGroupItem[]> => {
  const {data} = await apiClient.get(
    `${USER_MANAGEMENT_BASE_URL}/roles/${roleId}/projects/${projectId}/security-groups`
  );
  return data ?? [];
};

export const getBulkAccessList = async (
  payload: BulkAccessListRequest
): Promise<BulkAccessListResponse> => {
  const {data} = await apiClient.post<BulkAccessListResponse>(
    `${USER_MANAGEMENT_BASE_URL}/bulk-access/list`,
    payload
  );
  return data;
};
export const deleteUser = async (payload: IDeleteUserRequestPayload) => {
  const {data} = await apiClient.delete(`${USER_MANAGEMENT_BASE_URL}`, {
    data: payload,
  });
  return data;
};
export const removeUser = async ({payload, projectId}: IRemoveUserArgs) => {
  const {data} = await apiClient.delete(
    `${USER_MANAGEMENT_BASE_URL}/projects/${projectId}/removeuser${payload}`
  );
  return data;
};
export const getMeta = async (): Promise<IGetMetaResponse> => {
  const {data} = await apiClient.get(`${USER_MANAGEMENT_BASE_URL}/meta`);
  return data;
};

export const addUser = async (payload: IAddUserPayload) => {
  try {
    const {data} = await apiClient.post(
      `${USER_MANAGEMENT_BASE_URL}/adduser`,
      payload
    );
    return data;
  } catch (error) {
    throw parseApiError(
      error as AxiosError<BackendErrorData>,
      'UserManagement'
    );
  }
};

export const getNotLoggedInUsers = async (
  payload: IGetNotLoggedInUsersPayload
): Promise<IGetNotLoggedInUsersResponse> => {
  const {data} = await apiClient.post(
    `${USER_MANAGEMENT_BASE_URL}/pending`,
    payload
  );
  return data;
};

export const resendLink = async (
  payload: IResendLinkRequestPayload
): Promise<IResendLinkResponse> => {
  const {data} = await apiClient.post(
    `${USER_MANAGEMENT_BASE_URL}/users/${payload.userProjectLinkId}/resend-link`,
    payload
  );
  return data;
};

export const getBulkUserAccess = async (): Promise<IBulkUserAccessResponse> => {
  const {data} = await apiClient.get(
    `${USER_MANAGEMENT_BASE_URL}/bulk-access/view`
  );
  return data;
};

export const getBulkSecurityGroups = async (
  projectId: number
): Promise<ISecurityGroup> => {
  const {data} = await apiClient.get(
    `${USER_MANAGEMENT_BASE_URL}/projects/${projectId}/bulk/security-groups`
  );
  return data;
};

export const getBulkUserRights = async (
  payload: IGetBulkUserRightsPayload
): Promise<IBulkUserAccessResponse> => {
  const {data} = await apiClient.post(
    `${USER_MANAGEMENT_BASE_URL}/bulk-access/list`,
    payload
  );
  return data;
};

export const saveBulkAccess = async (
  payload: IGetBulkUserRightsPayload
): Promise<IBulkUserAccessResponse> => {
  const {data} = await apiClient.post(
    `${USER_MANAGEMENT_BASE_URL}/bulk-access`,
    payload
  );
  return data;
};

export const getUserRole = async (): Promise<IGetRolesResponse> => {
  const {data} = await apiClient.get<IGetRolesResponse>(
    `${USER_MANAGEMENT_BASE_URL}/role`
  );
  return data;
};

export const getUserById = async (
  userId: number,
  projectId?: number,
  entityId?: number
): Promise<IUserDetailsResponse> => {
  const queryParams = new URLSearchParams();

  if (projectId) {
    queryParams.append('projectId', String(projectId));
  }

  if (entityId) {
    queryParams.append('entityId', String(entityId));
  }

  const queryString = queryParams.toString();
  const url = `${USER_MANAGEMENT_BASE_URL}/${userId}${queryString ? `?${queryString}` : ''}`;

  const {data} = await apiClient.get(url);
  return data;
};

export const editUser = async ({
  userId,
  payload,
}: {
  userId: string | number;
  payload: IEditUserRequestPayload;
}) => {
  const url = `${USER_MANAGEMENT_BASE_URL}/${userId}`;
  const {data} = await apiClient.put(url, payload);
  return data;
};

export const changeUserRole = async (
  payload: IChangeUserRolePayload
): Promise<IChangeUserRoleResponse> => {
  const response = await apiClient.post(
    `${USER_MANAGEMENT_BASE_URL}/role`,
    payload
  );
  return response.data;
};

export const getSecurityGroupsByProject = async (
  projectId: number
): Promise<SecurityGroupItem[]> => {
  const {data} = await apiClient.get(
    `${USER_MANAGEMENT_BASE_URL}/projects/${projectId}/security-groups`
  );
  return data ?? [];
};
